import {
  ELSRestClientHelper,
  ELSCommonConfig,
  ELSTokenHelper,
  ELSLoggingService
} from '@els/els-ui-common-react';
import { AxiosResponse } from 'axios';
import { RecordObject } from '../models/common.models';

const fileName = 'api.utilities';

export const buildUrlFromBase = (url: string, addBaseUrl = true): string => {
  return addBaseUrl ? `${ELSCommonConfig.buildUrl}${url}` : url;
};

const withApiErrorLoggingOnly = <T = unknown>(promiseFunction: () => Promise<AxiosResponse<T>>): Promise<T> =>
  promiseFunction()
    .then(res => res.data)
    .catch((error: Error) => {
      // https://github.com/elsevierPTG/els-ui-common-react/blob/develop/module/services/LoggingService.js
      ELSLoggingService.error(fileName, error.message, error);
      throw error;
    });

/**
 * Status-preserving variant of withApiErrorLoggingOnly, for endpoints that have to
 * inspect the HTTP status (e.g. to tell a 204 "no content" apart from a 2xx with a
 * body) and so cannot have the response unwrapped to response.data for them.
 *
 * Error logging is identical, and deliberately shares this module's fileName so that
 * failures land under the same log source as every other API call.
 */
const withApiErrorLoggingOnlyRaw = <T = unknown>(
  promiseFunction: () => Promise<AxiosResponse<T>>
): Promise<AxiosResponse<T>> =>
  promiseFunction()
    .catch((error: Error) => {
      ELSLoggingService.error(fileName, error.message, error);
      throw error;
    });

export const getDefaultRequestHeaders = (headers = {}): RecordObject => {
  return {
    Authorization: `Bearer ${ELSTokenHelper.getToken()}`,
    accept: 'application/json, text/plain, */*',
    ...headers
  };
};

export const getDefaultRequestOptions = (options = {}, headers = {}): RecordObject => {
  return {
    headers: getDefaultRequestHeaders(headers),
    useWithCredentials: false,  // Default to not sending cookies
    ...options,  // options can override useWithCredentials if needed
  };
};

enum RestCall {
  get = 'get',
  post = 'post',
  put = 'put',
  patch = 'patch',
  delete = 'delete',
}

export type eolsBaseApiType = Record<
  RestCall,
  <T = unknown>(
    url: string,
    options?: RecordObject,
    headers?: RecordObject,
  ) => Promise<AxiosResponse<T>>
>

const eolsBaseApi: eolsBaseApiType = Object.values(RestCall).reduce((acc, cur) => {
  return {
    ...acc,
    [cur]: (url, options = {}, headers = {}) => {
      return ELSRestClientHelper[cur](url, getDefaultRequestOptions(options, headers));
    },
  };
}, {} as eolsBaseApiType);

export {
  withApiErrorLoggingOnly,
  withApiErrorLoggingOnlyRaw,
  eolsBaseApi
};
